import { AfterViewInit, Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { GoogleService } from "../../services/google.service";
import { GateService } from "../../services/gate.service";

@Component({
    selector: 'app-login',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule],
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss']
})
export class LoginComponent implements AfterViewInit {
  gateService = inject(GateService);
  googleService = inject(GoogleService);

  @ViewChild('googleSignInButton') googleSignInButton!: ElementRef<HTMLDivElement>

  loginForm: FormGroup;
  isLoading = false;
  errorMessage: string | null = null;

  // Design-related properties
  showPassword = false;
  rememberMe = false;

  constructor(
    private fb: FormBuilder,
    private router: Router
  ) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required]
    });
  }

  ngAfterViewInit() {
    this.googleService.onToken = this.handleGoogleResponse.bind(this);
    this.googleService.renderButton(this.googleSignInButton.nativeElement);
  }

  async handleGoogleResponse(token: any) {
    console.log(token);
    await this.gateService.loginWithGoogle(token);
  }

  async onSubmit(): Promise<void> {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    this.errorMessage = null;

    try {
      const { email, password } = this.loginForm.value;
      // await this.gatekeeper.login({ email, password });
      // No need to explicitly navigate, as Gatekeeper service handles redirection
      // after successful login based on configured loginRedirectUrl
    } catch (error) {
      this.errorMessage = this.getErrorMessage(error);
      this.isLoading = false;
    }
    // Don't use finally here as the Gatekeeper service manages the loading state
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  toggleRememberMe(): void {
    this.rememberMe = !this.rememberMe;
    // Store this preference - would need to be implemented in gatekeeper
  }

  getErrorMessage(error: any): string {
    console.error('Login error:', error);

    // Map error codes from your backend to user-friendly messages
    if (error?.error?.message) {
      return error.error.message;
    }

    if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
      return 'Invalid email or password. Please try again.';
    } else if (error.code === 'auth/too-many-requests') {
      return 'Too many failed login attempts. Please try again later.';
    } else if (error.code === 'auth/user-disabled') {
      return 'This account has been disabled. Please contact support.';
    }

    return 'An error occurred during login. Please try again.';
  }

  forgotPassword(): void {
    // Navigate to the forgot password page
    this.router.navigate(['/forgot-password']);
  }

  register(): void {
    // Navigate to the registration page
    this.router.navigate(['/register']);
  }
}
